import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL, createAuthHeader } from '../config/api.config';

const createFiltersSignature = (filters = {}) => {
  const keys = Object.keys(filters);
  if (keys.length === 0) {
    return '::';
  }

  const sortedKeys = keys.sort();

  const parts = sortedKeys.map((key) => {
    const rawValue = filters[key];

    if (Array.isArray(rawValue)) {
      const normalizedValues = [...rawValue]
        .map(value => (value ?? '').toString())
        .sort();

      return `${key}:[${normalizedValues.join(',')}]`;
    }

    const normalizedValue = rawValue ?? '';
    return `${key}:${normalizedValue.toString()}`;
  });

  return parts.join('|');
};

/**
 * Custom hook for managing dynamic experiment filters with faceted search
 * 
 * @param {string} clientId - The client ID for the API request
 * @param {Object} currentFilters - Current active filters (filteredInfo object)
 * @returns {Object} - { filterData, loading, error, refetchFilters }
 */
const useExperimentFilters = (clientId, currentFilters = {}) => {
  const [filterData, setFilterData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const lastFetchSignatureRef = useRef(null);

  useEffect(() => {
    lastFetchSignatureRef.current = null;
  }, [clientId]);

  /**
   * Fetch filter values from the API
   * Supports faceted search by passing current filters
   * @param {Object} filters - Current filters to apply
   */
  const fetchFilters = useCallback(async (filters = {}, options = {}) => {
    if (!clientId) {
      setFilterData(null);
      return;
    }

    const { force = false } = options;
    const signature = createFiltersSignature(filters);

    if (!force && signature === lastFetchSignatureRef.current) {
      return;
    }

    setLoading(true);
    setError(null);
    lastFetchSignatureRef.current = signature;

    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        throw new Error('No access token available');
      }

      // Build query parameters
      const params = new URLSearchParams();
      params.append('clientId', clientId);
      
      // Add filter parameters in the format the API expects: filters[key]=value
      Object.entries(filters).forEach(([key, values]) => {
        if (values && Array.isArray(values) && values.length > 0) {
          // For array filters (most filters)
          values.forEach(value => {
            params.append(`filters[${key}]`, value);
          });
        } else if (values && key === 'dynamicDateRange' && Array.isArray(values) && values.length === 2) {
          // Special handling for date range filter
          params.append(`filters[dateFrom]`, values[0]);
          params.append(`filters[dateTo]`, values[1]);
        }
      });

      const response = await axios.get(
        `${API_BASE_URL}/experiments/overview/filters?${params.toString()}`,
        {
          headers: createAuthHeader(token)
        }
      );

      setFilterData(response.data);
    } catch (err) {
      console.error('Error fetching experiment filters:', err);
      setError(err);
      // Set empty filter data on error to allow graceful degradation
      setFilterData({
        group: [],
        location: [],
        machineId: [],
        products: [],
        type: [],
        status: [],
        tags: [],
        dateRange: { min: null, max: null },
        routes: []
      });
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  // Fetch filters when clientId changes or when filters change (faceted search)
  useEffect(() => {
    if (clientId) {
      fetchFilters(currentFilters);
    }
  }, [clientId, currentFilters, fetchFilters]);

  return {
    filterData,
    loading,
    error,
    refetchFilters: fetchFilters
  };
};

export default useExperimentFilters;
